import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import * as ArchiveFilterUtil from 'archive/ArchiveFilterUtil';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import isArchived from 'archive/isArchived';
import * as benchlingUtil from 'BenchlingUtil';
import {setWindowTitle} from 'BenchlingUtil';
import {ReviewLifecycleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import WithReviewProcessHelpLink, {
  ReviewLifecycleHelpArticle,
} from 'notebook/reviews/review-lifecycles/WithReviewLifecycleHelpLink';
import {FONT_PROXIMA_11, FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import EditableItemTable, {RowProps} from 'ui-library/EditableItemTable';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import PlainSearchInput, {BorderRadius} from 'ui-library/PlainSearchInput';
import strUtil from 'util/strUtil';

import {
  ReviewLifecyclesSettingsPageQuery,
  ReviewLifecyclesSettingsPageQueryVariables,
} from './__generated/ReviewLifecyclesSettingsPageQuery';
import {onReviewLifecyclesArchive, onReviewLifecyclesUnarchive} from './lib';
import * as ReviewLifecycleDAO from './ReviewLifecyclesDAO';

type OrganizationGraphQL = ExtractByTypename<
  ReviewLifecyclesSettingsPageQuery['organizations'][number],
  'OrganizationNode'
>;

type ReviewLifecycleGraphQL = OrganizationGraphQL['reviewLifecycles'][number];

const COLUMN_WIDTH = 3;
const COLUMN_HEADERS = [
  {name: 'Name', width: COLUMN_WIDTH},
  {name: 'Organization', width: COLUMN_WIDTH},
  {name: 'Type', width: COLUMN_WIDTH},
  {name: 'Review Stage', width: COLUMN_WIDTH},
];

const {SELF_REVIEW, PARALLEL, SEQUENTIAL} = ReviewLifecycleType;

export const REVIEW_LIFECYCLE_TYPE_TO_DISPLAY_TEXT: {[K in ReviewLifecycleType]: string} = {
  [SELF_REVIEW]: 'Self-review',
  [SEQUENTIAL]: 'Sequential',
  [PARALLEL]: 'Parallel',
};

export const REVIEW_LIFECYCLES_SETTINGS_PAGE_QUERY = gql`
  query ReviewLifecyclesSettingsPageQuery($organizationIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    currentTenantOrganizations {
      admin
    }
    organizations(ids: $organizationIds) {
      ... on OrganizationNode {
        id
        name
        reviewLifecycles {
          id
          archiveRecord {
            purpose
          }
          organization {
            id
            name
          }
          currentVersion {
            id
            name
            lifecycleType
            reviewStages {
              name
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const EMPTY_SEARCH_QUERY = '';
const ALL_ORGANIZATIONS = 'All organizations';

interface Props {
  organizationIds: Array<string>;
}

interface ReviewLifecyclesListingProps {
  reviewLifecycles: Array<ReviewLifecycleGraphQL>;
  organizationNames: Array<string> | null;
  writable: boolean;
  refetchReviewLifecycles: () => Promise<void>;
}

function mapReviewLifecycleToRowValue(rlc: ReviewLifecycleGraphQL): Array<string> {
  const {currentVersion} = rlc;
  const lifecycleTypeDisplayText = REVIEW_LIFECYCLE_TYPE_TO_DISPLAY_TEXT[currentVersion.lifecycleType];
  return [
    currentVersion.name,
    rlc.organization.name,
    lifecycleTypeDisplayText,
    currentVersion.reviewStages.map((reviewStage) => reviewStage.name).join(', '),
  ];
}

function getFilteredSchemas(
  reviewLifecycles: Array<ReviewLifecycleGraphQL> | null,
  searchQuery: string,
  organizationFilter: string,
  archivePurposeFilters: Array<string>
): Array<ReviewLifecycleGraphQL> {
  const reviewLifecyclesWithNames = reviewLifecycles?.map((rlc) => ({...rlc, name: rlc.currentVersion.name}));
  const archiveFilteredReviewLifecyclesWithNames = reviewLifecyclesWithNames
    ? ArchiveFilterUtil.filterItemsToPurposes(reviewLifecyclesWithNames, archivePurposeFilters) || []
    : [];
  const archiveFilteredReviewLifecycles = reviewLifecycles
    ? reviewLifecycles.filter((rlc) =>
        archiveFilteredReviewLifecyclesWithNames.some((rlcWithName) => rlcWithName.id === rlc.id)
      )
    : [];
  const filteredReviewLifecycles = archiveFilteredReviewLifecycles
    ? archiveFilteredReviewLifecycles.filter(
        (rlc) =>
          strUtil.caseInsensitiveContains(rlc.currentVersion.name, searchQuery) &&
          (rlc.organization.name === organizationFilter || organizationFilter === ALL_ORGANIZATIONS)
      )
    : [];

  return filteredReviewLifecycles;
}

/** @package */
export function EmptyReviewLifecycleState({writable}: {writable: boolean}): JSX.Element {
  return (
    <div className={css(styles.emptyComponent, styles.centerColumnHorizontally, styles.centerVertically)}>
      <BenchlingIcon className={css(styles.icon, styles.darkGray)} icon="benchling" />
      <div className={css(styles.headerDescription)}>No Review Processes</div>
      {writable && (
        <div className={css(styles.subDescription, styles.darkGray)}>
          Review Processes allow you to define your own process for how reviewable items need to be reviewed
          and completed. You can create multi-stage reviews to capture many signatures or simple author based
          reviews to close reviews quickly.
        </div>
      )}
      <div className={css(styles.subDescription, styles.darkGray)}>
        You can learn more about Review Processes{' '}
        <WithReviewProcessHelpLink helpArticle={ReviewLifecycleHelpArticle.CREATE_ENTRY_REVIEW_PROCESS}>
          here
        </WithReviewProcessHelpLink>
        .
      </div>
      {writable && (
        <div className={css(styles.subDescription)}>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={() => {
              benchlingUtil.navigate(null, ReviewLifecycleDAO.configureReviewLifecycleURL());
            }}
            size={ButtonSize.SMALL}
            text="Create review process"
            dataTestAttrs={{component: 'EditableItemTable', element: 'createButton'}}
          />
        </div>
      )}
    </div>
  );
}

function ReviewLifecyclesListing({
  reviewLifecycles,
  organizationNames,
  writable,
  refetchReviewLifecycles,
}: ReviewLifecyclesListingProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState(EMPTY_SEARCH_QUERY);
  const [organizationFilter, setOrganizationFilter] = useState(ALL_ORGANIZATIONS);
  const [archivePurposeFilters, setArchivePurposeFilters] = useState([NOT_ARCHIVED_PURPOSE_STRING]);

  useOnMount(() => {
    setWindowTitle('Review processes');
  });

  const menuOptions = [{name: ALL_ORGANIZATIONS, value: ALL_ORGANIZATIONS}].concat(
    (organizationNames ?? []).map((name) => ({name, value: name}))
  );

  const filteredReviewLifecycles = getFilteredSchemas(
    reviewLifecycles,
    searchQuery,
    organizationFilter,
    archivePurposeFilters
  );

  function getReviewLifecycleUrlForRow(itemIndex: number): string {
    const rlc = filteredReviewLifecycles[itemIndex];
    return ReviewLifecycleDAO.reviewLifecycleURL(rlc.id);
  }

  const emptyMessage =
    reviewLifecycles.length === 0 ? (
      <EmptyReviewLifecycleState writable={writable} />
    ) : (
      'No review processes are present'
    );

  function handleChangeArchivePurposeFilters(selectedArchivePurposeFilters: Array<string>): void {
    setArchivePurposeFilters(selectedArchivePurposeFilters);
  }

  function handleReviewLifecyclesArchive(itemIndex: number): void {
    onReviewLifecyclesArchive([filteredReviewLifecycles[itemIndex]], refetchReviewLifecycles);
  }

  async function handleReviewLifecyclesUnarchive(itemIndex: number): Promise<void> {
    await onReviewLifecyclesUnarchive([filteredReviewLifecycles[itemIndex]], refetchReviewLifecycles);
  }

  const rows: Array<RowProps> = filteredReviewLifecycles.map((rlc, itemIndex) => {
    const row: RowProps = {
      id: rlc.id,
      itemIndex,
      values: mapReviewLifecycleToRowValue(rlc),
      isArchivable: true,
      isArchived: isArchived({...rlc, name: rlc.currentVersion.name}),
    };
    return row;
  });
  return (
    <div className="settingsPage">
      <div className="settingsPage-contentWrapper">
        <EditableItemTable
          tableName="Review processes"
          columnHeaders={COLUMN_HEADERS}
          customCreateButton={
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              text="Create"
              size={ButtonSize.SMALL}
              onClick={() => {
                benchlingUtil.navigate(null, ReviewLifecycleDAO.configureReviewLifecycleURL());
              }}
            />
          }
          emptyMessage={emptyMessage}
          isLoading={reviewLifecycles == null}
          rows={rows}
          getEditUrl={getReviewLifecycleUrlForRow}
          writable={writable}
          onArchive={handleReviewLifecyclesArchive}
          onUnarchive={handleReviewLifecyclesUnarchive}
          leftSubheaderNode={
            <div className="u-flex-display">
              <PlainSearchInput
                className={css(styles.searchInput)}
                borderRadius={BorderRadius.LARGE}
                placeholder="Search"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchQuery(e.target.value);
                }}
                onEscape={() => {
                  setSearchQuery(EMPTY_SEARCH_QUERY);
                }}
              />
              <SelectDropdown
                className={css(styles.marginRight)}
                displayName={organizationFilter}
                menu={
                  <SearchableMenu
                    onSelect={setOrganizationFilter}
                    optionDatas={menuOptions}
                    selectedValue={organizationFilter}
                  />
                }
                dataTestAttrs={{component: 'ReviewLifecyclesListing', element: 'root'}}
              />
              <ArchivePurposeDropdown
                itemIds={reviewLifecycles ? reviewLifecycles.map((rlc) => rlc.id) : []}
                onChangePurposes={handleChangeArchivePurposeFilters}
                selectedPurposes={archivePurposeFilters}
              />
            </div>
          }
        />
      </div>
    </div>
  );
}

function queryDataToReviewLifecycles({
  organizations: organizationsOrInaccessible,
  currentTenantOrganizations,
}: ReviewLifecyclesSettingsPageQuery): {
  organizationNames: Array<string>;
  reviewLifecycles: Array<ReviewLifecycleGraphQL>;
  isAdminOfAnyOrgs: boolean;
} {
  const organizations = organizationsOrInaccessible.filter(
    (organizationOrInaccessible): organizationOrInaccessible is OrganizationGraphQL =>
      !isInaccessible(organizationOrInaccessible)
  );
  return {
    organizationNames: organizations.map((organization) => organization.name),
    reviewLifecycles: organizations.map((organization) => organization.reviewLifecycles).flat(),
    isAdminOfAnyOrgs: currentTenantOrganizations.some((org) => org.admin === true) ?? false,
  };
}

export default function QueryingReviewLifecyclesSettingsPage(props: Props): JSX.Element {
  return (
    <BenchlingQuery<ReviewLifecyclesSettingsPageQuery, ReviewLifecyclesSettingsPageQueryVariables>
      query={REVIEW_LIFECYCLES_SETTINGS_PAGE_QUERY}
      variables={{organizationIds: props.organizationIds}}
      fetchPolicy="cache-and-network"
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data, {refetch}) => {
        const {organizationNames, reviewLifecycles, isAdminOfAnyOrgs} = queryDataToReviewLifecycles(data);
        return (
          <ReviewLifecyclesListing
            reviewLifecycles={reviewLifecycles}
            organizationNames={organizationNames}
            writable={isAdminOfAnyOrgs}
            refetchReviewLifecycles={async () => {
              await refetch();
            }}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  searchInput: {
    marginRight: 10,
    width: 200,
  },
  centerVertically: {
    display: 'flex',
    alignItems: 'center',
  },
  learnMoreText: {
    fontSize: '7px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  learnMoreButton: {
    padding: '0px 4px',
    marginLeft: '10px',
    height: '14px',
  },
  centerColumnHorizontally: {
    height: '70vh',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  emptyComponent: {
    paddingLeft: '30%',
    paddingRight: '30%',
  },
  headerDescription: {
    margin: '0px',
    color: NEAR_BLACK,
    ...FONT_PROXIMA_13,
  },
  darkGray: {
    color: GRAY_DARKER,
  },
  subDescription: {
    marginTop: '20px',
  },
  reviewDescription: {
    ...FONT_PROXIMA_11,
  },
  icon: {
    margin: '20px',
    width: '69px',
    height: '69px',
  },
  marginRight: {
    marginRight: 10,
  },
});
